// Pure helper kept outside the customElements guard so a Node harness can exercise it without a
// DOM (mirrors the shape used by the previous per-line picker's test harness).
function validateReturnRequest(reasonCode, note, strings) {
  if (!reasonCode) return strings.selectReasonError;
  if (reasonCode === 'other' && !String(note || '').trim()) return strings.noteRequiredError;
  return null;
}

if (!customElements.get('return-label-form')) {
  class ReturnLabelForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.message = this.querySelector('[data-return-label-message]');
      this.dialog = this.querySelector('[data-return-label-dialog]');
      this.button = this.querySelector('button[type="submit"]');
      this.buttonText = this.querySelector('[data-return-label-button-text]');
      this.reasonSelect = this.querySelector('[data-return-label-reason-select]');
      this.noteField = this.querySelector('[data-return-label-note-field]');
      this.noteInput = this.querySelector('[data-return-label-note]');
      this.pendingOrderNumber = null;
      this.pendingEmail = null;
      this.pendingReasonCode = null;
      this.pendingNote = null;
      this.returnLabelVariantId = Number(this.dataset.returnLabelVariantId);
      this.endpoint = this.dataset.endpoint;
      this.strings = this.getStrings();
    }

    connectedCallback() {
      if (!this.form || !this.endpoint || !this.returnLabelVariantId) return;

      this.form.addEventListener('submit', this.handleSubmit.bind(this));

      if (this.reasonSelect) {
        this.reasonSelect.addEventListener('change', this.handleReasonChange.bind(this));
        // Browsers can restore a previously chosen reason on back/forward navigation without firing
        // `change`, so sync the note field once on connect too.
        this.handleReasonChange();
      }

      if (this.dialog) {
        this.dialog.addEventListener('click', this.handleDialogClick.bind(this));
        this.dialog.addEventListener('click', this.handleDialogBackdropClick.bind(this));
      }
    }

    handleReasonChange() {
      const isOther = this.reasonSelect && this.reasonSelect.value === 'other';

      if (this.noteField) this.noteField.hidden = !isOther;
      if (this.noteInput && !isOther) this.noteInput.value = '';
    }

    async handleSubmit(event) {
      event.preventDefault();
      this.setMessage('');

      const formData = new FormData(this.form);
      const orderNumber = String(formData.get('orderNumber') || '').trim();
      const email = String(formData.get('email') || '').trim().toLowerCase();
      const reasonCode = this.reasonSelect ? this.reasonSelect.value : '';
      const note = this.noteInput ? this.noteInput.value : '';

      const validationError = validateReturnRequest(reasonCode, note, this.strings);
      if (validationError) {
        this.setMessage(validationError, 'error');
        const focusTarget = !reasonCode ? this.reasonSelect : this.noteInput;
        if (focusTarget) focusTarget.focus();
        return;
      }

      this.pendingOrderNumber = orderNumber;
      this.pendingEmail = email;
      this.pendingReasonCode = reasonCode;
      this.pendingNote = reasonCode === 'other' ? String(note || '').trim() : '';

      this.setLoading(true);

      try {
        const cart = await this.fetchJson('/cart.js');

        if (this.cartHasNonReturnLabelItems(cart)) {
          this.setLoading(false);
          this.showCartDialog();
          return;
        }

        await this.clearCart();
        await this.continueReturnLabelFlow();
      } catch (error) {
        this.showError(error);
        this.setLoading(false);
      }
    }

    async handleDialogClick(event) {
      const button = event.target.closest('button[data-action]');
      if (!button) return;

      if (button.dataset.action === 'cancel') {
        this.closeDialog();
        return;
      }

      if (button.dataset.action !== 'clear-and-continue') return;

      this.closeDialog();
      this.setMessage('');
      this.setLoading(true);

      try {
        await this.clearCart();
        await this.continueReturnLabelFlow();
      } catch (error) {
        this.showError(error);
        this.setLoading(false);
      }
    }

    handleDialogBackdropClick(event) {
      if (event.target === this.dialog) this.closeDialog();
    }

    cartHasNonReturnLabelItems(cart) {
      return (cart.items || []).some((item) => Number(item.variant_id) !== this.returnLabelVariantId);
    }

    showCartDialog() {
      if (this.dialog && typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
        return;
      }

      if (window.confirm(this.strings.cartConflictMessage)) {
        this.setLoading(true);
        this.clearCart()
          .then(() => this.continueReturnLabelFlow())
          .catch((error) => {
            this.showError(error);
            this.setLoading(false);
          });
      }
    }

    closeDialog() {
      if (this.dialog && this.dialog.open) this.dialog.close();
    }

    async continueReturnLabelFlow() {
      if (!this.pendingOrderNumber || !this.pendingEmail) throw new Error(this.strings.missingFormError);

      const body = {
        orderNumber: this.pendingOrderNumber,
        email: this.pendingEmail,
        reasonCode: this.pendingReasonCode
      };

      if (this.pendingReasonCode === 'other') body.note = this.pendingNote;

      const start = await this.fetchJson(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const variantId = Number(start.variantId) || this.returnLabelVariantId;
      const lineItemProperties = start.lineItemProperties || {
        _original_order_name: start.originalOrderName || start.orderName,
        _original_order_id: start.originalOrderId,
        _return_email: start.returnEmail || this.pendingEmail,
        _return_token: start.returnToken
      };

      await this.fetchJson('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: lineItemProperties
            }
          ]
        })
      });

      window.location.href = '/checkout';
    }

    async clearCart() {
      await this.fetchJson('/cart/clear.js', { method: 'POST' });
    }

    async fetchJson(url, options) {
      const response = await fetch(url, options);
      const text = await response.text();
      let data = {};

      try {
        data = text ? JSON.parse(text) : {};
      } catch (error) {
        if (!response.ok) throw new Error(this.strings.genericError);
        throw error;
      }

      if (!response.ok) {
        throw new Error(this.translateError(data));
      }

      return data;
    }

    getStrings() {
      const stringsEl = this.querySelector('[data-return-label-strings]');
      const fallback = {
        genericError: 'Something went wrong. Please try again.',
        missingFormError: 'Enter order number and email again.',
        invalidEmailError: 'Enter a valid email address.',
        invalidOrderNumberError: 'Enter the order number in the format SH1234 or 1234.',
        orderNotFoundError: 'We could not find an order with those details.',
        emailMismatchError: 'The email address does not match the order.',
        duplicateReturnLabelError: 'A return label has already been ordered for this order.',
        loadingLabel: 'Checking...',
        buttonLabel: 'Buy return label',
        cartConflictMessage: 'Return labels cannot be combined with other products. Empty the cart first and try again.',
        reasonLabel: 'Reason for the return',
        reasonPlaceholder: 'Choose a reason',
        noteLabel: 'Describe the reason',
        selectReasonError: 'Choose a reason for the return.',
        noteRequiredError: 'Describe the reason when you choose "Other".'
      };

      if (!stringsEl) return fallback;

      try {
        const parsed = JSON.parse(stringsEl.textContent);
        // Only sv and en.default are guaranteed to carry every key this page renders; other
        // locales show "Translation missing: ..." for keys they haven't caught up on yet. Drop
        // any such value here so the English fallback above is used instead of showing that
        // text to a customer on those storefronts.
        const clean = {};
        Object.keys(parsed || {}).forEach((key) => {
          const value = parsed[key];
          if (typeof value === 'string' && value && !/translation missing/i.test(value)) {
            clean[key] = value;
          }
        });
        return { ...fallback, ...clean };
      } catch (error) {
        return fallback;
      }
    }

    // The worker's error envelope now always carries { ok:false, code, error, message, detail }; when
    // `code` is present, `message` is already customer-ready Swedish and is shown verbatim. A response
    // with no `code` is a legacy shape (or an unrelated Shopify endpoint like /cart/add.js), so it
    // still goes through the original string-matching table.
    translateError(data) {
      if (data && typeof data === 'object' && data.code) {
        return typeof data.message === 'string' && data.message ? data.message : this.strings.genericError;
      }

      return this.translateLegacyError(data && (data.error || data.description || data.message));
    }

    translateLegacyError(message) {
      if (!message) return this.strings.genericError;

      const normalizedMessage = String(message).trim().toLowerCase();
      const messages = {
        'email must be a valid email address': this.strings.invalidEmailError,
        'ordernumber must be sh1234 or 1234': this.strings.invalidOrderNumberError,
        'order not found': this.strings.orderNotFoundError,
        'email does not match order': this.strings.emailMismatchError,
        'retursedel har redan beställts för den här ordern.': this.strings.duplicateReturnLabelError,
        'invalid json body': this.strings.genericError,
        'method not allowed': this.strings.genericError,
        'not found': this.strings.genericError,
        unauthorized: this.strings.genericError
      };

      if (messages[normalizedMessage]) return messages[normalizedMessage];
      if (/[åäöÅÄÖ]/.test(message)) return String(message);

      return this.strings.genericError;
    }

    setLoading(isLoading) {
      if (this.button) {
        this.button.disabled = isLoading;
        this.button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      }

      if (this.buttonText) {
        this.buttonText.textContent = isLoading ? this.strings.loadingLabel : this.strings.buttonLabel;
      }
    }

    setMessage(message, type = '') {
      if (!this.message) return;
      this.message.textContent = message;
      this.message.dataset.type = type;
    }

    showError(error) {
      const message = error instanceof Error ? error.message : this.strings.genericError;
      this.setMessage(message, 'error');
    }
  }

  customElements.define('return-label-form', ReturnLabelForm);
}
